<template>
  <section class="workoutBindingPanel" aria-label="合集编排">
    <header class="workoutBindingPanel__head">
      <h3 class="workoutBindingPanel__title">合集编排</h3>
      <p class="workoutBindingPanel__hint">
        动作、音乐、语音分三个独立区块配置，可分别挑选与排序；动作区最多 {{ zoneLimits.actions }} 条，音乐区最多
        {{ zoneLimits.music }} 条，语音区最多 {{ zoneLimits.voices }} 条。
      </p>
      <p v-if="bindingRefreshing" class="workoutBindingPanel__hint workoutBindingPanel__hint--sync">
        正在同步子资源最新信息…
      </p>
    </header>

    <workout-collection-binding-zone
      v-for="zone in bindingZones"
      :key="zone.id"
      :ref="'zone-' + zone.id"
      :zone="zone"
      :rows="rowsForZone(zone.id)"
      :max-bindings="zone.maxBindings"
      :field-labels="fieldLabels"
      @update:rows="updateZoneRows(zone.id, $event)"
      @pick="openPicker(zone.id)"
    />

    <workout-binding-picker-dialog
      :visible.sync="pickerVisible"
      :title="activePickerMeta.pickerTitle"
      :asset-type="activePickerMeta.assetType"
      :exclude-keys="pickerExcludeKeys"
      @confirm="handlePickerConfirm"
    />
  </section>
</template>

<script>
import WorkoutBindingPickerDialog from './WorkoutBindingPickerDialog.vue'
import WorkoutCollectionBindingZone from './WorkoutCollectionBindingZone.vue'
import { WORKOUT_BINDING_FIELD_LABELS, MAX_BINDINGS_BY_ZONE } from './workoutFormConstants'
import {
  BINDING_ZONES,
  findBindingZone,
  mapPickedRow,
} from './workoutCollectionBindingModel'
import {
  applyBindingRefreshToLatestRows,
  bindingRowsChanged,
  mergeActionRowWithRefMeta,
  normalizeMusicLoopForRow,
} from './workoutActionBindingCalculator'
import {
  fetchBindingRefMetaByDbId,
  refreshBindingRowsForZone,
} from './workoutBindingFetch'
import './workoutCollectionBinding.css'

/**
 * 合集三区 binding 编排：Picker、增删、ref_meta 补全。
 * 进入编辑 / 切回标签 / 打开挑选弹窗前都会拉服务端最新子资源，避免运营对着过期时长配置。
 */
export default {
  name: 'WorkoutCollectionBindingPanel',
  components: { WorkoutBindingPickerDialog, WorkoutCollectionBindingZone },
  props: {
    actions: {
      type: Array,
      default: () => [],
    },
    music: {
      type: Array,
      default: () => [],
    },
    voices: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      bindingZones: BINDING_ZONES,
      fieldLabels: WORKOUT_BINDING_FIELD_LABELS,
      zoneLimits: MAX_BINDINGS_BY_ZONE,
      pickerVisible: false,
      pickerZone: 'actions',
      bindingRefreshing: false,
      bindingRefreshSeq: 0,
    }
  },
  computed: {
    activePickerMeta() {
      return findBindingZone(this.pickerZone)
    },
    pickerExcludeKeys() {
      return (this.rowsForZone(this.pickerZone) || []).map((row) => row.resource_id).filter(Boolean)
    },
  },
  mounted() {
    this.onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this.refreshAllBindingsFromServer({ silent: true })
      }
    }
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  activated() {
    if (this.onVisibilityChange) {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }
    this.refreshAllBindingsFromServer({ silent: true })
  },
  deactivated() {
    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
  },
  beforeDestroy() {
    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
  },
  methods: {
    rowsForZone(zoneId) {
      if (zoneId === 'music') {
        return this.music
      }
      if (zoneId === 'voices') {
        return this.voices
      }
      return this.actions
    },
    updateZoneRows(zoneId, rows) {
      this.$emit(`update:${zoneId}`, rows)
    },
    zoneAtCapacity(zoneId) {
      const meta = findBindingZone(zoneId)
      return this.rowsForZone(zoneId).length >= meta.maxBindings
    },
    async openPicker(zoneId) {
      const meta = findBindingZone(zoneId)
      if (this.zoneAtCapacity(zoneId)) {
        this.$message.warning(`「${meta.title}」最多绑定 ${meta.maxBindings} 条`)
        return
      }
      await this.refreshBindingZoneFromServer(zoneId, { silent: true })
      this.pickerZone = zoneId
      this.pickerVisible = true
    },
    handlePickerConfirm(rows) {
      const zoneId = this.pickerZone
      const meta = this.activePickerMeta
      const list = this.rowsForZone(zoneId)
      const existing = new Set(list.map((row) => row.resource_id))
      const room = meta.maxBindings - list.length
      if (room <= 0) {
        this.$message.warning(`「${meta.title}」最多绑定 ${meta.maxBindings} 条`)
        return
      }
      const unbound = rows.filter((row) => row.key && !existing.has(row.key))
      const toAdd = unbound.slice(0, room)
      if (!toAdd.length) {
        return
      }
      const assetType = this.activePickerMeta.assetType
      const loading = this.$loading({
        lock: true,
        text: '加载资源详情…',
        spinner: 'el-icon-loading',
      })
      Promise.all(toAdd.map((row) => this.enrichPickedRow(zoneId, assetType, row)))
        .then((mappedRows) => {
          this.updateZoneRows(zoneId, [...list, ...mappedRows])
          if (unbound.length > toAdd.length) {
            this.$message.warning(
              `「${meta.title}」最多绑定 ${meta.maxBindings} 条，已按上限截断`
            )
          }
        })
        .catch(() => {
          this.$message.error('加载资源详情失败，请重试')
        })
        .finally(() => {
          loading.close()
        })
    },
    async enrichPickedRow(zoneId, assetType, pickerRow) {
      let mapped = mapPickedRow(zoneId, pickerRow)
      if (!pickerRow.id) {
        return mapped
      }
      try {
        const refMeta = await fetchBindingRefMetaByDbId(assetType, pickerRow.id)
        if (!refMeta) {
          return mapped
        }
        if (zoneId === 'actions') {
          return mergeActionRowWithRefMeta(mapped, refMeta)
        }
        return normalizeMusicLoopForRow({ ...mapped, ref_meta: refMeta })
      } catch (e) {
        return mapped
      }
    },
    async refreshBindingZoneFromServer(zoneId, options = {}) {
      const snapshotRows = this.rowsForZone(zoneId)
      if (!snapshotRows.length) {
        return false
      }
      const expectedSeq = options.expectedSeq
      const refreshed = await refreshBindingRowsForZone(zoneId, snapshotRows)
      // 过期 refresh 不得盖掉用户已编辑的编排参数
      if (expectedSeq != null && expectedSeq !== this.bindingRefreshSeq) {
        return false
      }
      // 合并到请求返回时的最新行，避免飞行期间改过的 repeat/rest/loop 被快照冲掉
      const latestRows = this.rowsForZone(zoneId)
      const merged = applyBindingRefreshToLatestRows(
        zoneId,
        snapshotRows,
        latestRows,
        refreshed
      )
      const changed = bindingRowsChanged(zoneId, latestRows, merged)
      if (changed) {
        this.updateZoneRows(zoneId, merged)
        if (options.notifyOnChange) {
          this.$message.info('已同步子资源最新信息，编排参数已自动重算')
        }
      }
      return changed
    },
    /**
     * 三区 binding 全量 refresh：配置态主入口（加载、切回标签、保存前兜底）。
     */
    async refreshAllBindingsFromServer(options = {}) {
      const { silent = true, notifyOnChange = false } = options
      const seq = ++this.bindingRefreshSeq
      if (!silent) {
        this.bindingRefreshing = true
      }
      let anyChanged = false
      try {
        const changedFlags = await Promise.all(
          BINDING_ZONES.map((zone) =>
            this.refreshBindingZoneFromServer(zone.id, {
              notifyOnChange: false,
              expectedSeq: seq,
            })
          )
        )
        if (seq !== this.bindingRefreshSeq) {
          return false
        }
        anyChanged = changedFlags.some(Boolean)
        if (anyChanged && notifyOnChange) {
          this.$message.info('已同步子资源最新信息，编排参数已自动重算')
        }
      } finally {
        if (seq === this.bindingRefreshSeq) {
          this.bindingRefreshing = false
        }
      }
      return anyChanged
    },
    /** @deprecated 兼容旧调用；等价于 refreshAllBindingsFromServer({ silent: true }) */
    async refreshMissingRefMeta() {
      return this.refreshAllBindingsFromServer({ silent: true })
    },
    /** @deprecated 兼容旧调用；等价于 refreshAllBindingsFromServer({ silent: true }) */
    async syncActionBindingsFromServer(options = {}) {
      return this.refreshAllBindingsFromServer({
        silent: options.silent !== false,
        notifyOnChange: options.silent === false,
      })
    },
    reinitZoneSortables() {
      BINDING_ZONES.forEach((zone) => {
        const ref = this.$refs[`zone-${zone.id}`]
        const component = Array.isArray(ref) ? ref[0] : ref
        if (component && component.reinitSortable) {
          component.reinitSortable()
        }
      })
    },
  },
}
</script>
